"use client";

import { useCallback } from "react";
import { useAnalytics } from "./useAnalytics";
import { ANALYTICS_EVENTS } from "./events";
import {
  buildSignupEventProps,
  buildInfoGatheringEventProps,
  buildSocialMediaManualEventProps,
  buildNicheManualEventProps,
  buildProfileBuilderEventProps,
  buildProfileAppealEventProps,
  buildCreatorOnboardingEventProps,
} from "./helpers";

/**
 * Custom hook for onboarding analytics
 * Provides clean, type-safe methods for tracking onboarding events
 */
export function useOnboardingAnalytics() {
  const { trackEvent } = useAnalytics();

  /**
   * Track CR_SIGNUP event
   */
  const trackSignup = useCallback(
    (socialMedia: string, creator?: any) => {
      const props = buildSignupEventProps(socialMedia, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SIGNUP, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_INFO_GATHERING event
   */
  const trackInfoGathering = useCallback(
    (
      statuses?: {
        creator_profile?: "SUCCESS" | "FAIL";
        creator_niche?: "SUCCESS" | "FAIL";
        creator_jobs?: "SUCCESS" | "FAIL";
      },
      creator?: any
    ) => {
      const props = buildInfoGatheringEventProps(statuses, creator);
      trackEvent(ANALYTICS_EVENTS.CR_INFO_GATHERING, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SOCIALMEDIA_MANUAL event
   */
  const trackSocialMediaManual = useCallback(
    (
      data: {
        social_media: string;
        social_media_handle?: string;
        social_media_url?: string;
      },
      creator?: any
    ) => {
      const props = buildSocialMediaManualEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SOCIALMEDIA_MANUAL, {
        ...props,
        $set: { primaryPlatform: data?.social_media },
      });
    },
    [trackEvent]
  );

  /**
   * Track CR_NICHE_MANUAL event
   */
  const trackNicheManual = useCallback(
    (
      data: {
        primary_niche: string;
        secondary_niche?: string[];
        keywords?: string[];
      },
      creator?: any
    ) => {
      const props = buildNicheManualEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_NICHE_MANUAL, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_PROFILE_BUILDER event
   */
  const trackProfileBuilder = useCallback(
    (
      data: {
        status: "approved" | "rejected" | "waitlist" | "pending_review";
        artha_link?: string;
        primary_niche?: string;
        secondary_niche?: string[];
        keywords?: string[];
        username?: string;
      },
      creator?: any
    ) => {
      const props = buildProfileBuilderEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_PROFILE_BUILDER, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_PROFILE_APPEAL event
   */
  const trackProfileAppeal = useCallback(
    (
      data: {
        rejection_reason?: string;
        appeal_message?: string;
      },
      creator?: any
    ) => {
      const props = buildProfileAppealEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_PROFILE_APPEAL, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_ONBOARDING_COMPLETE event
   */
  const trackOnboardingComplete = useCallback(
    (creator?: any) => {
      const props = buildCreatorOnboardingEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_ONBOARDING_COMPLETE, props);
    },
    [trackEvent]
  );

  return {
    trackSignup,
    trackInfoGathering,
    trackSocialMediaManual,
    trackNicheManual,
    trackProfileBuilder,
    trackProfileAppeal,
    trackOnboardingComplete,
  };
}
